/**
 * The one Finix secure tokenization form (card or bank account) for both apps,
 * and the one module that loads finix.js (BL-2459, moved from bidder-web's
 * `FinixHostedForm` + `finix-script-loader`; ADR 067 — Processor Contract § 7).
 *
 * Card and bank fields render inside a Finix-domain iframe, so a raw card or
 * account number never touches our DOM or server (PCI-DSS SAQ A). The form
 * mounts `Finix.PaymentForm` into its container and hands the resulting `TK…`
 * token to `onToken`.
 *
 * Two variants, because Finix v2 renders the fields AND (optionally) its submit
 * button in ONE cross-origin iframe that page CSS can never reach:
 * - `own-button` (default): Finix renders its own button, themed through
 *   `styles.default.submitButton`, which the SDK applies inside the iframe. Hiding
 *   that button with CSS and drawing ours beside it showed TWO buttons (BUG-1069).
 * - `external-submit`: the caller's own button calls this form's `submit()`
 *   handle, which calls Finix's documented `form.submit(callback)`. The form is
 *   mounted without `onSubmit`, which tells finix.js to render no button at all
 *   (verified against the live sandbox, 2026-10-13).
 *
 * With a `merchantId`, the form opens a Finix fraud session (`Finix.Auth`) for
 * that merchant and returns its id with the token, for the charge's
 * `fraud_session_id` (BL-2460).
 *
 * The card/bank FIELDS keep Finix's own type scale (BL-740). Each app supplies its
 * look through props, never through imports: the submit colours and the alert
 * dialect (ADR 030 — Design Canon). The props are provider config, variant names
 * and callbacks, never domain values (ADR 029 — View Units).
 *
 * The iframe never mounts against the local `mock` provider. Point a tenant at a
 * Finix sandbox application to exercise it live.
 * Reference: https://docs.finix.com/guides/online-payments/payment-tokenization/tokenization-forms
 */
'use client';

import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState, type MutableRefObject } from 'react';

import { Alert, type AlertDialect } from './Alert';

/** The finix.js v2 script. No other production module may name this URL (finix-boundary guard). */
const FINIX_SCRIPT_SRC = 'https://js.finix.com/v/2/finix.js';

/** Which hosted form to mount: card fields plus billing address, or a bank account. */
export type SecureFormMethod = 'card' | 'ach';

/** Fill and text colour for Finix's own submit button, already validated by the caller. */
export interface SecureFormSubmitColors {
    /** Button fill, e.g. the house brand colour. */
    background: string;
    /** Text colour on that fill. */
    text: string;
}

/** What arrives with a token besides the token itself. */
export interface SecureFormTokenMeta {
    /**
     * The Finix fraud session id, when a `merchantId` was given and Finix's
     * session call has answered. It is `undefined` without a `merchantId`, and
     * also when a token arrives before the session does (Finix opens the session
     * at mount, so in practice it is back long before a buyer finishes typing).
     * If the session call fails, finix.js substitutes a locally generated
     * `session-key-…` value, which is passed through unchanged.
     */
    fraudSessionId: string | undefined;
}

/** Props every variant takes. */
export interface SecureCardFormBaseProps {
    /** The house's Finix application id (from the tenant's payment config). */
    applicationId: string;
    /** Finix environment of that application. */
    environment: 'sandbox' | 'production';
    /** Card (default) or bank account. */
    method?: SecureFormMethod;
    /** The house's Finix merchant id. When given, a fraud session id comes back with the token. */
    merchantId?: string;
    /**
     * Called with the Finix token (`TK…`) on a successful tokenization. Tokens
     * expire 30 minutes after creation, so the caller saves or charges promptly.
     */
    onToken: (providerToken: string, meta: SecureFormTokenMeta) => void;
    /** Called with a message when tokenization or the script load fails. */
    onError?: (message: string) => void;
    /**
     * The caller already shows `onError`'s message itself, on every call,
     * unconditionally — so the form's own load-failure banner would duplicate
     * it (BL-2460 RAC 3 reopen). Default `false`: the form's own banner is the
     * only display a load failure gets unless the caller opts out because it
     * has one of its own. Don't set this if `onError` only reacts while a
     * submit is in flight (e.g. an awaited tokenizer) — a load failure before
     * any press would then show nowhere.
     */
    hideLoadFailureAlert?: boolean;
    /** The app's alert appearance for the load-failure banner. */
    alertDialect?: AlertDialect;
    /**
     * Automation handle on the mount container. Defaults to `secure-card-form`:
     * a page shows one secure form at a time. `data-method` says which form it is.
     */
    testId?: string;
}

/** Finix renders and owns the submit button. */
export interface SecureCardFormOwnButtonProps extends SecureCardFormBaseProps {
    variant?: 'own-button';
    /** Text of Finix's submit button, e.g. "Save card". */
    submitLabel: string;
    /** Colours for Finix's submit button. Absent: Finix's default button. */
    submitColors?: SecureFormSubmitColors;
}

/** The caller renders its own button and calls the form's `submit()` handle. */
export interface SecureCardFormExternalSubmitProps extends SecureCardFormBaseProps {
    variant: 'external-submit';
}

/** Props for {@link SecureCardForm}. */
export type SecureCardFormProps = SecureCardFormOwnButtonProps | SecureCardFormExternalSubmitProps;

/** The handle an `external-submit` form exposes through its ref. */
export interface SecureCardFormHandle {
    /**
     * Ask Finix to tokenize what the buyer entered. The result arrives through
     * `onToken` or `onError`, exactly as with Finix's own button.
     */
    submit: () => void;
}

/**
 * The payment-method names Finix's hosted form understands. This is Finix's
 * vocabulary, not ours: our bank-account method is `'ach'`, Finix's is `'bank'`.
 * The form validates its fields against the list for the method it is given, so
 * any other value (BUG-2084: `'ach'`) checks a bank form against the card fields
 * and its submit button never enables.
 */
type FinixPaymentMethod = 'card' | 'bank';

/**
 * Our method name → Finix's. The translation lives here, at the SDK boundary, so
 * Finix's `'bank'` never leaks into our own vocabulary (ADR 004 — Vendor
 * Gateways). Sending our `'ach'` straight through left "Save bank account"
 * disabled forever (BUG-2084).
 */
const FINIX_PAYMENT_METHOD: Record<SecureFormMethod, FinixPaymentMethod> = {
    card: 'card',
    ach: 'bank',
};

/** Callback Finix invokes with `(error, response)` after a tokenization attempt. */
type FinixResultCallback = (error: unknown, response: unknown) => void;

/** The `Finix.PaymentForm` instance methods this form calls. */
interface FinixPaymentFormInstance {
    /** Tokenize from a button outside the iframe (Finix's documented `form.submit`). */
    submit?: (callback: FinixResultCallback) => void;
    /** Optional teardown: not every Finix SDK build exposes it. */
    destroy?: () => void;
}

/** Options passed to `Finix.PaymentForm(...)`, scoped to what this form sends. */
interface FinixPaymentFormOptions {
    paymentMethods: FinixPaymentMethod[];
    showAddress?: boolean;
    submitLabel?: string;
    styles?: { default?: { submitButton?: Record<'default' | 'disabled', Record<string, string | number>> } };
    /** Absent tells finix.js to render no submit button (external submit). */
    onSubmit?: FinixResultCallback;
}

declare global {
    interface Window {
        /** Set by finix.js once loaded. Finix publishes no TypeScript types for it. */
        Finix?: {
            PaymentForm: (
                containerId: string,
                environment: 'sandbox' | 'production',
                applicationId: string,
                options: FinixPaymentFormOptions,
            ) => FinixPaymentFormInstance;
            /** Opens a fraud session for a merchant; the callback receives its id. */
            Auth?: (
                environment: 'sandbox' | 'production',
                merchantId: string,
                callback: (sessionKey: string) => void,
            ) => { getSessionKey?: () => string | undefined };
        };
    }
}

const NOT_READY_MESSAGE = 'The secure form is still loading. Try again in a moment.';

/**
 * The form's own words for a failure, per method (BL-2460, approved by Michael
 * Lawrie 2026-10-14). Never the processor's or the browser's text (rule #5):
 * staff and buyers act on these, and a raw error tells them nothing they can do.
 */
export const SECURE_FORM_MESSAGES: Record<SecureFormMethod, { loadFailed: string; tokenizeFailed: string }> = {
    card: {
        loadFailed: 'Couldn’t load the secure card form. Refresh and try again.',
        tokenizeFailed: 'Check the card details and try again.',
    },
    ach: {
        loadFailed: 'Couldn’t load the secure bank account form. Refresh and try again.',
        tokenizeFailed: 'Check the bank account details and try again.',
    },
};

let finixScriptLoading: Promise<void> | null = null;

/**
 * Resolve once `window.Finix` is available, injecting the script at most once.
 * SSR-safe (resolves at once without a window). Rejects when the script fails
 * to load (network, CSP), which the form surfaces.
 *
 * @returns A promise that settles when finix.js has loaded or failed.
 * @sideEffect Appends one `<script>` to `document.body` on the first call.
 */
function loadFinixScript(): Promise<void> {
    if (typeof window === 'undefined') return Promise.resolve();
    if (window.Finix) return Promise.resolve();
    if (finixScriptLoading) return finixScriptLoading;
    finixScriptLoading = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = FINIX_SCRIPT_SRC;
        script.async = true;
        script.onload = () => {
            finixScriptLoading = null;
            resolve();
        };
        script.onerror = () => {
            finixScriptLoading = null;
            reject(new Error(`Failed to load Finix.js from ${FINIX_SCRIPT_SRC}`));
        };
        document.body.appendChild(script);
    });
    return finixScriptLoading;
}

/**
 * The `styles.default.submitButton` theme for Finix's button. The disabled state
 * (form invalid) keeps the fill at reduced opacity so it reads as the same button.
 *
 * @param colors - The caller's colours, or none for Finix's default.
 * @returns The Finix `styles` option, or undefined.
 */
function submitButtonStyles(colors: SecureFormSubmitColors | undefined): FinixPaymentFormOptions['styles'] {
    if (!colors) return undefined;
    const { background: backgroundColor, text: color } = colors;
    return {
        default: {
            submitButton: {
                default: { backgroundColor, color },
                disabled: { backgroundColor, color, opacity: 0.5 },
            },
        },
    };
}

/**
 * The Finix mount options for a variant. External submit sends no `onSubmit`,
 * label or theme, so finix.js renders no button.
 *
 * @param props - The form's props.
 * @param onResult - The shared result funnel.
 * @returns The options for `Finix.PaymentForm`.
 */
function mountOptions(props: SecureCardFormProps, onResult: FinixResultCallback): FinixPaymentFormOptions {
    const method = props.method ?? 'card';
    const base = { paymentMethods: [FINIX_PAYMENT_METHOD[method]], showAddress: method === 'card' };
    if (props.variant === 'external-submit') return base;
    return { ...base, submitLabel: props.submitLabel, styles: submitButtonStyles(props.submitColors), onSubmit: onResult };
}

/** Refs the mount effect and the result funnel share. */
interface FinixFormRefs {
    instance: MutableRefObject<FinixPaymentFormInstance | null>;
    sessionKey: MutableRefObject<string | undefined>;
    onResult: MutableRefObject<FinixResultCallback>;
}

/**
 * Load finix.js, mount the form once, and open the fraud session when a
 * merchant id is given. Tears the iframe down on unmount.
 *
 * @param opts.containerId - The element Finix mounts into.
 * @param opts.props - The form's props, read once at mount.
 * @param opts.refs - The shared instance, session and funnel refs.
 * @param opts.onLoadError - Called when finix.js fails to load.
 * @param opts.mountedRef - True while the form is mounted.
 * @sideEffect Injects finix.js, mounts Finix's iframe, opens a Finix fraud session.
 */
function useFinixMount(opts: {
    containerId: string;
    props: SecureCardFormProps;
    refs: FinixFormRefs;
    onLoadError: () => void;
    mountedRef: MutableRefObject<boolean>;
}): void {
    const { containerId, props, refs, onLoadError, mountedRef } = opts;
    useEffect(() => {
        let cancelled = false;
        mountedRef.current = true;
        loadFinixScript()
            .then(() => {
                const finix = window.Finix;
                if (cancelled || !finix?.PaymentForm) return;
                // Strict Mode runs the effect twice; key off the DOM so Finix's
                // iframe mounts into the container only once.
                const container = document.getElementById(containerId);
                if (!container || container.childElementCount > 0) return;
                refs.instance.current = finix.PaymentForm(containerId, props.environment, props.applicationId, mountOptions(props, (e, r) => refs.onResult.current(e, r)));
                if (props.merchantId != null && finix.Auth) {
                    finix.Auth(props.environment, props.merchantId, (key) => { refs.sessionKey.current = key; });
                }
            })
            .catch(() => {
                if (!cancelled) onLoadError();
            });
        return () => {
            cancelled = true;
            mountedRef.current = false;
            // Tear down the iframe so its DOM and listeners don't leak across toggles.
            try {
                refs.instance.current?.destroy?.();
            } catch {
                /* An SDK build without destroy(): nulling the ref below is enough. */
            }
            refs.instance.current = null;
        };
        // Mount once (empty deps on purpose): the props are read at mount, as Finix
        // offers no re-configure.
    }, []);
}

/**
 * Mounts the Finix hosted tokenization form and hands its token up, through
 * Finix's own (themed) button or the caller's via the `submit()` handle.
 *
 * @param props - {@link SecureCardFormProps}
 * @param ref - The {@link SecureCardFormHandle}, for `external-submit`.
 * @returns The form container, or, on a load failure, its own banner — unless
 *   `hideLoadFailureAlert` is set, in which case nothing (the caller's own
 *   `onError`-driven error already shows it).
 */
export const SecureCardForm = forwardRef<SecureCardFormHandle, SecureCardFormProps>(function SecureCardForm(props, ref) {
    const method = props.method ?? 'card';
    // `useId()`, never `Math.random()`: a random id differs between the server
    // render and hydration. Colons are stripped because Finix may hand the id to
    // `querySelector`.
    const containerId = `finix-${method}-form-${useId().replace(/:/g, '')}`;
    const [scriptFailed, setScriptFailed] = useState(false);

    // Callbacks live in refs so the mount-time closure reads current values
    // without re-mounting the iframe.
    const onTokenRef = useRef(props.onToken);
    const onErrorRef = useRef(props.onError);
    useEffect(() => { onTokenRef.current = props.onToken; }, [props.onToken]);
    useEffect(() => { onErrorRef.current = props.onError; }, [props.onError]);

    // True only while mounted: a token that arrives after the form closed (the
    // buyer shut the modal or flipped card/bank) is dropped, never saved.
    const mountedRef = useRef(true);
    const messages = SECURE_FORM_MESSAGES[method];
    const refs: FinixFormRefs = {
        instance: useRef<FinixPaymentFormInstance | null>(null),
        sessionKey: useRef<string | undefined>(undefined),
        onResult: useRef<FinixResultCallback>(() => {}),
    };
    refs.onResult.current = (error, response) => {
        if (!mountedRef.current) return;
        // Our words, never Finix's: the field-level detail is already shown inside its iframe.
        const tokenId = error ? undefined : (response as { data?: { id?: string } })?.data?.id;
        if (!tokenId) {
            onErrorRef.current?.(messages.tokenizeFailed);
            return;
        }
        onTokenRef.current(tokenId, { fraudSessionId: refs.sessionKey.current });
    };

    useFinixMount({
        containerId,
        props,
        refs,
        mountedRef,
        onLoadError: () => {
            setScriptFailed(true);
            onErrorRef.current?.(messages.loadFailed);
        },
    });

    useImperativeHandle(ref, () => ({
        submit: () => {
            const instance = refs.instance.current;
            if (!instance?.submit) {
                // BL-2642: a form whose script failed will never load, so it says so instead of "still loading".
                onErrorRef.current?.(scriptFailed ? messages.loadFailed : NOT_READY_MESSAGE);
                return;
            }
            instance.submit((error, response) => refs.onResult.current(error, response));
        },
    }));

    if (scriptFailed) {
        // BL-2460 RAC 3 reopen: a caller whose onError always shows the message
        // itself opts out with hideLoadFailureAlert, instead of this banner
        // doubling it. The default stays "show it" — a caller whose onError
        // only reacts to a press in flight (an awaited tokenizer) would
        // otherwise show nothing for a load failure nobody pressed into yet.
        if (props.hideLoadFailureAlert) return null;
        return (
            <Alert tone="danger" dialect={props.alertDialect}>
                {messages.loadFailed}
            </Alert>
        );
    }

    return (
        <div
            id={containerId}
            // 300px holds room for Finix's iframe until it mounts, so the form doesn't jump.
            // Carried from the first secure form (#547); no measurement of the iframe is recorded.
            className="min-h-[300px]"
            data-testid={props.testId ?? 'secure-card-form'}
            data-method={method}
        />
    );
});
